import {css} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NameTemplatePartNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DraggableNameTemplate from 'name-templates/DraggableNameTemplate';
import {
  nameTemplateModalStyles,
  NameTemplatePart,
  NameTemplatePartType,
  NameTemplateType,
  nameTemplateTypeToFriendlyName,
} from 'name-templates/NameTemplateConstants';
import {
  getSupportedNameTemplatePartOptions,
  makeExampleForNameTemplate,
} from 'name-templates/NameTemplateUtil';
import {SubmenuContext} from 'name-templates/PartPredicatesUtil';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getNonArchivedOrComputedFields} from 'schemas/fields/SchemaFieldGetters';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {InputChangeEvent} from 'util/Types';

import {
  PossibleRequestV2DefinitionTemplatePartOptionsQuery,
  PossibleRequestV2DefinitionTemplatePartOptionsQueryVariables,
} from './__generated/PossibleRequestV2DefinitionTemplatePartOptionsQuery';
import {
  PossibleSchemaInterfaceNameTemplatePartOptionsQuery,
  PossibleSchemaInterfaceNameTemplatePartOptionsQueryVariables,
} from './__generated/PossibleSchemaInterfaceNameTemplatePartOptionsQuery';
import {
  PossibleStudySchemaTemplatePartOptionsQuery,
  PossibleStudySchemaTemplatePartOptionsQueryVariables,
} from './__generated/PossibleStudySchemaTemplatePartOptionsQuery';
import {
  PossibleTagSchemaTemplatePartOptionsQuery,
  PossibleTagSchemaTemplatePartOptionsQueryVariables,
} from './__generated/PossibleTagSchemaTemplatePartOptionsQuery';
import NameTemplateTypeConfiguration from './NameTemplateTypeConfiguration';

export interface SetNameTemplateModalProps {
  // expected to be a ModelTypeConstants value or an empty string if there's no relevant entity type
  bioentityType: string;
  schemaFields: Array<SchemaField | ClientSchemaField>;
  schemaId: string;
  sequenceType: SequenceSchemaType | null;
  initialNameTemplateParts: Array<NameTemplatePart>;
  initialShouldOrderNamePartsBySequence?: boolean; // undefined if checkbox should be hidden
  itemType: TagSchemaItemType | null;
  onRequestSubmit: (submitArgs: {
    nameTemplateParts: Array<NameTemplatePart>;
    shouldOrderNamePartsBySequence?: boolean;
  }) => void;
  onRequestClose: () => void;
  nameTemplateType: NameTemplateType;
  nameTemplateTypeConfiguration?: NameTemplateTypeConfiguration;
}

export interface PresentationalSetNameTemplateModalProps extends SetNameTemplateModalProps {
  possibleNameTemplatePartOptions: Array<NameTemplatePartNode>;
}

interface State {
  nameTemplateParts: Array<NameTemplatePart>;
  shouldOrderNamePartsBySequence?: boolean; // undefined if checkbox should be hidden
  draggingNameTemplatePartIndex: number;
}

export const SET_NAME_TEMPLATE_MODAL_KEY = 'set-name-template-modal';

const POSSIBLE_SCHEMA_INTERFACE_NAME_TEMPLATE_PART_OPTIONS_QUERY = gql`
  query PossibleSchemaInterfaceNameTemplatePartOptionsQuery(
    $schemaId: StrictString!
    $nameTemplateType: NameTemplateType!
  ) @skipAudit @engComponent(name: SCHEMAS) {
    schema: schemaInterface(id: $schemaId) {
      ... on SchemaInterfaceNode {
        id
        possibleNameTemplateParts(nameTemplateType: $nameTemplateType) {
          datetimeFormat
          fieldId
          text
          type
          displayName
          exampleValue
          typeSpecificConfiguration
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const POSSIBLE_TAG_SCHEMA_TEMPLATE_PART_OPTIONS_QUERY = gql`
  query PossibleTagSchemaTemplatePartOptionsQuery(
    $schemaId: StrictString!
    $nameTemplateType: NameTemplateType!
  ) @skipAudit @engComponent(name: SCHEMAS) {
    schema: tagSchema(id: $schemaId) {
      ... on TagSchemaNode {
        id
        possibleNameTemplateParts(nameTemplateType: $nameTemplateType) {
          datetimeFormat
          fieldId
          text
          type
          displayName
          exampleValue
          typeSpecificConfiguration
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const POSSIBLE_STUDY_SCHEMA_TEMPLATE_PART_OPTIONS_QUERY = gql`
  query PossibleStudySchemaTemplatePartOptionsQuery(
    $schemaId: StrictString!
    $nameTemplateType: NameTemplateType!
  ) @skipAudit @engComponent(name: SCHEMAS) {
    schema: studySchema(id: $schemaId) {
      ... on StudySchemaNode {
        id
        possibleNameTemplateParts(nameTemplateType: $nameTemplateType) {
          datetimeFormat
          fieldId
          text
          type
          displayName
          exampleValue
          typeSpecificConfiguration
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const POSSIBLE_REQUEST_V2_DEFINITION_TEMPLATE_PART_OPTIONS_QUERY = gql`
  query PossibleRequestV2DefinitionTemplatePartOptionsQuery(
    $schemaId: StrictString!
    $nameTemplateType: NameTemplateType!
  ) @skipAudit @engComponent(name: WORKFLOWS) {
    schema: requestV2Definition(id: $schemaId) {
      ... on RequestV2DefinitionNode {
        id
        possibleNameTemplateParts(nameTemplateType: $nameTemplateType) {
          datetimeFormat
          fieldId
          text
          type
          displayName
          exampleValue
          typeSpecificConfiguration
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type QueryResult =
  | PossibleSchemaInterfaceNameTemplatePartOptionsQuery
  | PossibleTagSchemaTemplatePartOptionsQuery
  | PossibleStudySchemaTemplatePartOptionsQuery
  | PossibleRequestV2DefinitionTemplatePartOptionsQuery;

type QueryVars =
  | PossibleSchemaInterfaceNameTemplatePartOptionsQueryVariables
  | PossibleTagSchemaTemplatePartOptionsQueryVariables
  | PossibleStudySchemaTemplatePartOptionsQueryVariables
  | PossibleRequestV2DefinitionTemplatePartOptionsQueryVariables;

function getQueryForSchemaType(schemaId: string): DocumentNode {
  if (APIUtil.isSchemaInterface(schemaId)) {
    return POSSIBLE_SCHEMA_INTERFACE_NAME_TEMPLATE_PART_OPTIONS_QUERY;
  }
  if (APIUtil.isTagSchema(schemaId)) {
    return POSSIBLE_TAG_SCHEMA_TEMPLATE_PART_OPTIONS_QUERY;
  }
  if (APIUtil.isRequestV2Definition(schemaId)) {
    return POSSIBLE_REQUEST_V2_DEFINITION_TEMPLATE_PART_OPTIONS_QUERY;
  }
  return POSSIBLE_STUDY_SCHEMA_TEMPLATE_PART_OPTIONS_QUERY;
}

export default function QueryingSetNameTemplateModal(props: SetNameTemplateModalProps): JSX.Element | null {
  const query = getQueryForSchemaType(props.schemaId);

  // Use cache-and-network because many schema changes can invalidate old possible name template part options.
  const result = useBenchlingQuery<QueryResult, QueryVars>(query, {
    variables: {
      schemaId: props.schemaId,
      nameTemplateType: props.nameTemplateType,
    },
    fetchPolicy: 'cache-and-network',
  });
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (result.type === QueryResultType.ERROR) {
    // Show error message and close modal
    notifications.error('Failed to get name template part options.');
    props.onRequestClose();
    return null;
  }

  let possibleNameTemplatePartOptions: Array<NameTemplatePartNode> = [];
  if (result.data?.schema) {
    if (isInaccessible(result.data.schema)) {
      notifications.error('Failed to get name template part options.');
      possibleNameTemplatePartOptions = [];
      props.onRequestClose();
      return null;
    } else {
      possibleNameTemplatePartOptions = result.data.schema.possibleNameTemplateParts;
    }
  }

  return (
    <SetNameTemplateModal {...props} possibleNameTemplatePartOptions={possibleNameTemplatePartOptions} />
  );
}

export class SetNameTemplateModal extends React.PureComponent<
  PresentationalSetNameTemplateModalProps,
  State
> {
  override state: State = {
    nameTemplateParts: this.props.initialNameTemplateParts,
    shouldOrderNamePartsBySequence: this.props.initialShouldOrderNamePartsBySequence,
    draggingNameTemplatePartIndex: -1,
  };

  dropdown?: SelectDropdown | null;

  private handleChangeOrderSequenceParts = (e: InputChangeEvent): void => {
    this.setState({shouldOrderNamePartsBySequence: e.target.checked});
  };

  appendPart(part: NameTemplatePart): void {
    this.setState({nameTemplateParts: [...this.state.nameTemplateParts, part]});
  }

  private handleRemovePart = (index: number): void => {
    this.setState({nameTemplateParts: reactUpdate(this.state.nameTemplateParts, {$splice: [[index, 1]]})});
  };

  private handleDragStart = (index: number): void => {
    this.setState({draggingNameTemplatePartIndex: index});
  };

  private createSubmenuContext = (): SubmenuContext => ({
    itemType: this.props.itemType,
  });

  private handleDrop = (index: number): void => {
    const {draggingNameTemplatePartIndex} = this.state;
    const nameTemplatePart = this.state.nameTemplateParts[draggingNameTemplatePartIndex];
    const reorderedNameTemplateParts = [...this.state.nameTemplateParts];
    reorderedNameTemplateParts.splice(draggingNameTemplatePartIndex, 1);
    reorderedNameTemplateParts.splice(index, 0, nameTemplatePart);
    this.setState({nameTemplateParts: reorderedNameTemplateParts});
  };

  private handleSelectItem = (callback: () => void): void => {
    if (this.dropdown) {
      closeSelectDropdown(this.dropdown);
    }
    callback();
  };

  private validateNameTemplateParts = (): string | null => {
    const {nameTemplateParts} = this.state;
    const nameTemplateWillProduceEmptyName =
      nameTemplateParts.length &&
      nameTemplateParts.every((nameTemplatePart) => nameTemplatePart.type === NameTemplatePartType.SEPARATOR);
    return nameTemplateWillProduceEmptyName
      ? 'This name template will produce an empty name because it only includes separators. Please include at least one non-separator component.'
      : null;
  };

  private handleSubmit = (): void => {
    const {onRequestSubmit, onRequestClose, initialNameTemplateParts, initialShouldOrderNamePartsBySequence} =
      this.props;
    const {nameTemplateParts, shouldOrderNamePartsBySequence} = this.state;
    const didNameTemplatePartsChange = !isEqual(initialNameTemplateParts, nameTemplateParts);
    const didShouldOrderNamePartsBySequenceChange =
      initialShouldOrderNamePartsBySequence !== shouldOrderNamePartsBySequence;
    if (!(didNameTemplatePartsChange || didShouldOrderNamePartsBySequenceChange)) {
      onRequestClose();
      return;
    }
    onRequestSubmit({nameTemplateParts, shouldOrderNamePartsBySequence});
  };

  override render(): JSX.Element {
    const {bioentityType, sequenceType, onRequestClose} = this.props;
    const {nameTemplateParts, shouldOrderNamePartsBySequence} = this.state;
    const invalidNameTemplateError = this.validateNameTemplateParts();
    return (
      <Modal
        title={`Set ${nameTemplateTypeToFriendlyName(this.props.nameTemplateType, this.props.nameTemplateTypeConfiguration)} name template`}
        onRequestClose={onRequestClose}
        onRequestSubmit={this.handleSubmit}
        buttons={[
          {
            text: 'Set',
            disabled: !nameTemplateParts.length || invalidNameTemplateError != null,
            submit: true,
            dataTestAttrs: {component: 'SetNameTemplateModal', element: 'setButton'},
          },
        ]}
      >
        {this.props.nameTemplateTypeConfiguration?.getDisplayBannerText && (
          <div className="u-small-spacing-bottom">
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text={this.props.nameTemplateTypeConfiguration?.getDisplayBannerText(
                this.props.nameTemplateType
              )}
            />
          </div>
        )}
        <div className={classNames('u-small-spacing-bottom', css(nameTemplateModalStyles.helpText))}>
          You can re-order components by dragging them.
        </div>
        <div className={css(nameTemplateModalStyles.partChips)}>
          <DraggableNameTemplate
            draggable={true}
            handleDragStart={this.handleDragStart}
            handleDrop={this.handleDrop}
            nameTemplateParts={nameTemplateParts}
            schemaFields={getNonArchivedOrComputedFields(this.props.schemaFields)}
            onRemovePart={this.handleRemovePart}
          />
        </div>
        <div className="u-small-spacing-bottom">
          <SelectDropdown
            displayName="New component..."
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                onSelect={this.handleSelectItem}
                optionDatas={getSupportedNameTemplatePartOptions(
                  this.props.nameTemplateType,
                  this.props.schemaFields,
                  (part: NameTemplatePart) => {
                    this.appendPart(part);
                  },
                  this.props.possibleNameTemplatePartOptions,
                  this.createSubmenuContext()
                )}
              />
            }
            ref={(node) => {
              this.dropdown = node;
            }}
          />
        </div>
        {nameTemplateParts.length > 0 && (
          <div className={classNames('u-overflow-wrap-break-word', css(nameTemplateModalStyles.example))}>
            <span className={css(nameTemplateModalStyles.exampleLabel)}>Preview: </span>
            <span className={css(nameTemplateModalStyles.exampleContent)}>
              {makeExampleForNameTemplate(nameTemplateParts, [
                ...getNonArchivedOrComputedFields(this.props.schemaFields),
              ])}
            </span>
          </div>
        )}
        {shouldOrderNamePartsBySequence !== undefined &&
          bioentityType === ModelTypeConstants.SEQUENCE &&
          sequenceType !== SequenceSchemaType.OLIGO && (
            <div className="u-small-spacing-top">
              <label>
                <input
                  type="checkbox"
                  onChange={this.handleChangeOrderSequenceParts}
                  checked={shouldOrderNamePartsBySequence}
                />
                If one or many part link fields are included in the name template, list parts in the order
                they appear on the sequence map, sorted by start position and then end position.
              </label>
            </div>
          )}
        {invalidNameTemplateError != null && (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={invalidNameTemplateError} />
        )}
      </Modal>
    );
  }
}
